import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  CalendarToday,
  Phone,
  EventNote,
  AttachMoney,
  ReceiptLong,
  Email,
  LinkedIn,
  Chat,
} from "@mui/icons-material";
import apiClient from "@/api/client";

type Period = "today" | "week" | "month";

interface AgendaItem {
  type: "communication" | "followup" | "opportunity_start" | "invoice_outstanding";
  id: string;
  datetime: string;
  title: string;
  company_name: string | null;
  link: string | null;
  icon_hint?: string;
  priority?: string;
  engagement_type?: string;
  value?: number | null;
}

interface AgendaResponse {
  period: Period;
  start: string;
  end: string;
  items: AgendaItem[];
}

const PERIOD_LABELS: Record<Period, string> = {
  today: "Today",
  week: "This Week",
  month: "This Month",
};

const ITEM_TYPE_LABELS: Record<AgendaItem["type"], string> = {
  communication: "Communication",
  followup: "Follow-up",
  opportunity_start: "Deal start",
  invoice_outstanding: "Invoice due",
};

const ITEM_TYPE_COLORS: Record<
  AgendaItem["type"],
  "default" | "primary" | "secondary" | "success" | "warning"
> = {
  communication: "primary",
  followup: "warning",
  opportunity_start: "success",
  invoice_outstanding: "secondary",
};

function ItemIcon({ item }: { item: AgendaItem }) {
  const sx = { fontSize: 18, color: "text.secondary" };
  if (item.type === "communication") {
    switch (item.icon_hint) {
      case "email":
        return <Email sx={sx} />;
      case "call":
        return <Phone sx={sx} />;
      case "linkedin":
        return <LinkedIn sx={sx} />;
      case "meeting":
        return <EventNote sx={sx} />;
      default:
        return <Chat sx={sx} />;
    }
  }
  if (item.type === "followup") return <EventNote sx={sx} />;
  if (item.type === "opportunity_start") return <AttachMoney sx={sx} />;
  if (item.type === "invoice_outstanding") return <ReceiptLong sx={sx} />;
  return <CalendarToday sx={sx} />;
}

function formatItemDate(item: AgendaItem, period: Period): string {
  const d = new Date(item.datetime);
  if (period === "today") {
    // Show time if available (communications have datetime; followups/
    // opportunities are date-only — show "today" instead).
    if (item.datetime.includes("T")) {
      return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    }
    return "Today";
  }
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

export default function AgendaWidget() {
  const [period, setPeriod] = useState<Period>("week");
  const navigate = useNavigate();

  const { data, isLoading } = useQuery<AgendaResponse>({
    queryKey: ["dashboard-agenda", period],
    queryFn: async () => {
      const res = await apiClient.get<AgendaResponse>(`/dashboard/agenda?period=${period}`);
      return res.data;
    },
  });

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <CalendarToday color="action" />
          <Typography variant="h6" sx={{ flex: 1 }}>
            Agenda
          </Typography>
        </Box>
        <Tabs value={period} onChange={(_, v) => setPeriod(v as Period)} sx={{ mb: 2 }}>
          <Tab value="today" label={PERIOD_LABELS.today} />
          <Tab value="week" label={PERIOD_LABELS.week} />
          <Tab value="month" label={PERIOD_LABELS.month} />
        </Tabs>

        {isLoading && <CircularProgress size={20} />}

        {data && data.items.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            Nothing scheduled for {PERIOD_LABELS[period].toLowerCase()}. Log a communication or set
            a follow-up to populate this view.
          </Typography>
        )}

        {data && data.items.length > 0 && (
          <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
            {data.items.map((item) => (
              <Box
                key={`${item.type}-${item.id}`}
                onClick={() => item.link && navigate(item.link)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  cursor: item.link ? "pointer" : "default",
                  "&:hover": item.link ? { bgcolor: "action.hover" } : undefined,
                }}
              >
                <Box sx={{ width: 60, color: "text.secondary", fontSize: 13, flexShrink: 0 }}>
                  {formatItemDate(item, period)}
                </Box>
                <ItemIcon item={item} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {item.title}
                  </Typography>
                  {item.company_name && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {item.company_name}
                      {item.value != null ? ` · £${item.value.toLocaleString()}` : ""}
                    </Typography>
                  )}
                </Box>
                <Chip
                  label={ITEM_TYPE_LABELS[item.type]}
                  size="small"
                  color={ITEM_TYPE_COLORS[item.type]}
                  variant="outlined"
                  sx={{ flexShrink: 0 }}
                />
              </Box>
            ))}
          </Box>
        )}

        {data && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
            Window: {data.start} → {data.end} · communications, follow-ups, deal starts, outstanding
            invoices
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
